import { useDeferredValue, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useIsMobile } from "@/components/shared/PagePrimitives";
import { ListHeader, ListToolbar } from "@/components/shared/ListToolbar";
import { useListFilters } from "@/hooks/useListFilters";
import { useGroupSort, GroupedGrid } from "@/components/shared/GroupSort";
import { SupplierEditDialog } from "@/components/suppliers/SupplierEditDialog";
import { useAdresarView, AdresarSeznam, KontaktBunka } from "@/components/shared/Adresar";
import { Truck, Mail, Phone, List, LayoutGrid, Package, MapPin, CaseSensitive, CalendarClock, Hash, Clock } from "lucide-react";
import { plur, formatDate } from "@/lib/format";

const norm = (s) => (s || "").normalize("NFKD").replace(/[̀-ͯ]/g, "").toLowerCase();

// 314: Dodavatelé — stejně jako Investoři: karty firem, IČO z ARES, výběr v objednávce.
// 317: pohled Seznam (výchozí) / Karty.
// 353: jednotná lišta — Filtr Objednávky, Nový dodavatel (N).
// 378: Seskupit a řadit (jako Doklady) — skupiny Objednávky / Město, řazení Název / Naposledy / Počet objednávek / Datum přidání.
const GROUPS = {
  orders: { label: "Objednávky", icon: Package, key: (s) => (s.orders_open > 0 ? "a" : s.orders_total ? "b" : ""), order: (a, b) => a.localeCompare(b),
    name: (k) => ({ a: "S rozpracovanou objednávkou", b: "Jen vyřízené" }[k]), none: "Bez objednávky", dirs: ["Rozpracované první", "Bez objednávky první"] },
  city: { label: "Město", icon: MapPin, key: (s) => (s.city || "").trim(), none: "Bez města" },
};
const byName = (a, b) => (a.name || "").localeCompare(b.name || "", "cs");
const ORDERS = {
  name: { label: "Název", icon: CaseSensitive, dirs: ["A → Z", "Z → A"], def: "asc", val: (s) => s.name || "" },
  last: { label: "Naposledy objednáno", icon: CalendarClock, dirs: ["Nejdávněji", "Nejnověji"], def: "desc", val: (s) => s.last_order_at || "" },
  orders: { label: "Počet objednávek", icon: Hash, dirs: ["Od nejmenšího", "Od největšího"], def: "desc", val: (s) => s.orders_total || 0 },
  created: { label: "Datum přidání", icon: Clock, dirs: ["Nejstarší", "Nejnovější"], def: "desc", val: (s) => s.created_at || "" },
};
const VIEWS = [{ value: "list", label: "Seznam", icon: List, testid: "suppliers-view-list" }, { value: "cards", label: "Karty", icon: LayoutGrid, testid: "suppliers-view-cards" }];

export default function Suppliers() {
  const mobile = useIsMobile();
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [view, setView] = useAdresarView("suppliers_view");
  const q = norm(useDeferredValue(search).trim());

  const load = () => api.get("/suppliers").then((r) => setItems(r.data || [])).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const groups = useMemo(() => [{ key: "objednavky", label: "Objednávky", icon: Package,
    options: [{ value: "rozpracovane", label: "S rozpracovanou objednávkou" }, { value: "zadne", label: "Bez objednávky" }],
    match: (s, v) => (v === "rozpracovane" ? s.orders_open > 0 : !s.orders_total) }], []);
  const F = useListFilters(items || [], { groups, entity: { one: "dodavatel", few: "dodavatelé", many: "dodavatelů" } });
  const found = useMemo(() => F.visible.filter((s) => !q || norm([s.name, s.ico, s.city, s.contact_person, s.email, s.phone].join(" ")).includes(q)), [F.visible, q]);
  const GS = useGroupSort(found, { storageKey: "suppliers_order", legacyKey: "suppliers_sort", groups: GROUPS, orders: ORDERS, def: { g: "none", gd: 0, k: "name", dir: "asc" }, tie: byName });
  const shown = GS.sorted;
  const open = (s) => nav(`/dodavatele/${s.id}`);

  return (
    <div className="space-y-5" data-testid="suppliers-page">
      <ListHeader title="Dodavatelé" mobile={mobile} testid="suppliers-header"
        summary={items ? `${plur(items.length, "firma", "firmy", "firem")} · u koho objednáváte materiál` : null}
        search={search} onSearch={setSearch} searchPh="Hledat název, IČO, město…"
        primary={{ label: "Dodavatel", full: "Nový dodavatel", onClick: () => setCreateOpen(true), testid: "new-supplier-btn" }} />

      {(items?.length || 0) > 0 && (
        <ListToolbar mobile={mobile} testid="suppliers-toolbar" filter={F} search={search} onSearch={setSearch} searchPh="Hledat název, IČO, město…"
          sort={GS.sortProp}
          views={VIEWS} view={view} onView={setView} />
      )}

      {shown.length > 0 && view === "list" && (
        <AdresarSeznam rows={shown} onOpen={open} testid="suppliers-list" rowTestid={(s) => `supplier-${s.id}`} groups={GS.groups} closed={GS.closed} onToggleGroup={GS.toggle}
          cols={[
            { label: "Dodavatel", width: "minmax(0,2fr)", cell: (s) => (<>
              <span className="block font-bold text-[15px] truncate">{s.name}</span>
              {s.contact_person && <span className="block text-muted-foreground truncate">{s.contact_person}</span>}
            </>) },
            { label: "IČO", width: "110px", cell: (s) => <span className="tabular-nums">{s.ico || <span className="text-muted-foreground">—</span>}</span> },
            { label: "Sídlo", width: "minmax(0,1fr)", cell: (s) => <span className="block truncate">{s.city || <span className="text-muted-foreground">—</span>}</span> },
            { label: "Kontakt", width: "minmax(0,1.4fr)", cell: (s) => <KontaktBunka email={s.email} phone={s.phone} /> },
            { label: "Objednávky", width: "130px", right: true, cell: (s) => s.orders_total ? (<>
              <span className="block font-bold tabular-nums">{s.orders_total}</span>
              {s.orders_open > 0 && <span className="block text-xs pp-ink font-semibold tabular-nums">{plur(s.orders_open, "rozpracovaná", "rozpracované", "rozpracovaných")}</span>}
            </>) : <span className="text-muted-foreground">—</span> },
            { label: "Naposledy", width: "100px", right: true, cell: (s) => <span className="tabular-nums whitespace-nowrap">{s.last_order_at ? formatDate(s.last_order_at) : <span className="text-muted-foreground">—</span>}</span> },
          ]}
          mobile={(s) => ({
            title: s.name,
            sub: [s.ico && `IČO ${s.ico}`, s.city, s.contact_person].filter(Boolean).join(" · ") || s.email || s.phone,
            side: s.orders_total ? plur(s.orders_total, "objednávka", "objednávky", "objednávek") : null,
          })} />
      )}

      {shown.length > 0 && view === "cards" && (
        <GroupedGrid groups={GS.groups} rows={shown} closed={GS.closed} onToggle={GS.toggle} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {(s) => (
            <button key={s.id} onClick={() => open(s)} data-testid={`supplier-${s.id}`}
              className="text-left bg-card border border-border rounded-[20px] p-5 hover:border-brand hover:-translate-y-0.5 transition-all motion-reduce:transition-none motion-reduce:hover:translate-y-0">
              <div className="flex items-center gap-2 mb-2 min-w-0"><Truck size={16} className="text-brand shrink-0" /><span className="font-bold truncate">{s.name}</span></div>
              {s.contact_person && <div className="text-sm text-muted-foreground truncate">{s.contact_person}</div>}
              {(s.ico || s.city) && <div className="text-xs text-muted-foreground truncate">{[s.ico && `IČO ${s.ico}`, s.city].filter(Boolean).join(" · ")}</div>}
              <div className="mt-3 space-y-1 text-sm">
                {s.email && <div className="flex items-center gap-2 text-muted-foreground min-w-0"><Mail size={13} className="shrink-0" /><span className="truncate">{s.email}</span></div>}
                {s.phone && <div className="flex items-center gap-2 text-muted-foreground"><Phone size={13} className="shrink-0" /> {s.phone}</div>}
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5 text-[12px] font-semibold">
                <span className="px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">{s.orders_total ? plur(s.orders_total, "objednávka", "objednávky", "objednávek") : "zatím bez objednávky"}</span>
                {s.orders_open > 0 && <span className="px-2 py-0.5 rounded-full bg-brand/10 pp-ink">{plur(s.orders_open, "rozpracovaná", "rozpracované", "rozpracovaných")}</span>}
                {(s.branches || []).length > 0 && <span className="px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">{plur(s.branches.length, "pobočka", "pobočky", "poboček")}</span>}
              </div>
            </button>
          )}
        </GroupedGrid>
      )}
      {items && items.length === 0 && (
        <p className="text-muted-foreground" data-testid="suppliers-empty">
          Zatím žádní dodavatelé. Přidejte firmu podle IČO — nebo se založí sama, když ji napíšete do objednávky.
        </p>
      )}
      {items && items.length > 0 && !shown.length && <p className="text-muted-foreground">Nic nenalezeno.</p>}

      <SupplierEditDialog open={createOpen} onOpenChange={setCreateOpen} onSaved={(s) => nav(`/dodavatele/${s.id}`)} />
    </div>
  );
}
